'use client';

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { completeSelfOnboard, SelfOnboardApiError } from '@/services/self-onboard.service';
import {
  markSelfOnboardComplete,
  readSelfOnboardSetup,
} from '@/lib/public-routing';

export default function SelfOnboardSetPasswordPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [setup, setSetup] = useState<{ sessionId: string; setupToken: string } | null>(null);

  useEffect(() => {
    const stored = readSelfOnboardSetup();
    if (!stored) {
      navigate(`/self-onboard/${token}/verify-email`, { replace: true });
      return;
    }
    setSetup(stored);
  }, [navigate, token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setup) return;
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await completeSelfOnboard({
        sessionId: setup.sessionId,
        setupToken: setup.setupToken,
        password,
        confirmPassword,
      });
      markSelfOnboardComplete();
      navigate('/self-onboard/thank-you', { replace: true });
    } catch (err) {
      setError(err instanceof SelfOnboardApiError ? err.message : 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8">
      <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Create your password</h1>
      <p className="text-sm text-slate-500 mb-6">Choose a strong password for your HRMS account.</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Password *</span>
          <div className="relative mt-1">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2.5 pr-10"
              required
              minLength={8}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
              onClick={() => setShowPassword((v) => !v)}
            >
              <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
            </button>
          </div>
        </label>

        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Confirm password *</span>
          <input
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2.5"
            required
            minLength={8}
          />
        </label>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-[var(--hrm-primary)] to-[var(--hrm-primary-dark)] text-white font-semibold disabled:opacity-60"
        >
          {submitting ? 'Creating account…' : 'Complete registration'}
        </button>
      </form>

      <Link
        to={`/self-onboard/${token}/verify-email`}
        className="mt-4 inline-block text-sm text-slate-500 hover:text-slate-700"
      >
        ← Back
      </Link>
    </div>
  );
}
